import { useState, useCallback } from "react";
import {
  DirectBillingPayerGroupDto,
  CreateInsuranceTransactionDto,
} from "../../../../../../api-client";
import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { transactionMethods } from "../../../../Kasse/v2/types/kasse.types";
import { BillingTypeEnum } from "../../../Base/types/insuranceBills.types";
import { useCreateGkvBill } from "../../../../../../stores/Transactions/insurance-mutation";

export interface UseGkvBillSaveProps {
  bills: DirectBillingPayerGroupDto[];
  notes: Record<string, string>;
  account: string;
  userId: string;
}

export interface UseGkvBillSaveReturn {
  isSaving: boolean;
  saveBills: () => Promise<string[] | undefined>;
}

/**
 * Handles saving the current bill list as insurance transactions.
 *
 * Iterates over payer groups, builds `CreateInsuranceTransactionDto` per group,
 * and calls `createTransaction` for each. Returns the created transaction IDs.
 */
export function useGkvBillSave({
  bills,
  notes,
  account,
  userId,
}: UseGkvBillSaveProps): UseGkvBillSaveReturn {
  const [isSaving, setIsSaving] = useState(false);
  const createGkvBill = useCreateGkvBill();
  // Bridge: update MobX store's lastUpdate so the old bills table refetches
  const transactionStore = useStore(TransactionsContext);

  const saveBills = useCallback(async () => {
    if (bills.length === 0) {
      notificationStore.showMessage("Keine Rechnungen zum Speichern", "warning");
      return;
    }

    try {
      setIsSaving(true);
      const createdIds: string[] = [];

      for (const group of bills) {
        const {
          payerIK,
          payerName,
          paperReceivingCenter,
          digitalReceivingCenter,
          rxs,
          totalPrice,
        } = group;

        const rxIds = rxs.map((rx) => rx.id).filter((id): id is string => !!id);

        const dto: CreateInsuranceTransactionDto = {
          payerId: payerIK,
          accountId: account,
          amount: parseFloat(totalPrice.toFixed(2)),
          userId,
          method: transactionMethods.VERSICHERUNG,
          rxIds,
          paperCenterId: paperReceivingCenter?.ik || "",
          dataCenterId: digitalReceivingCenter?.ik || "",
          notes: notes[payerIK] || "",
          tAccountId: rxs[0]?.tarifGroupId === "Privat" ? 4105 : 4101,
          billType: BillingTypeEnum.GKV,
        };

        const created = await createGkvBill.mutateAsync(dto);
        createdIds.push(created.id);
      }

      notificationStore.showMessage(
        "Alle Abrechnungstransaktionen wurden erfolgreich erstellt und gespeichert.",
        "success",
      );

      // Signal MobX store so the old InsuranceBillsTable refetches
      transactionStore.lastUpdate = new Date();

      return createdIds;
    } catch (error) {
      console.error("Error creating transactions", error);
      notificationStore.showMessage("Fehler beim Erstellen der Transaktionen", "error");
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [bills, notes, account, userId, createGkvBill]);

  return { isSaving, saveBills };
}
